<template>
  <div
    v-if="breadcrumbs.length"
    aria-label="breadcrumbs"
    class="flex items-center text-gray-900 text-sm pb-2 lg:pb-4 outer-page-padding-x wide-page-max-width"
  >
    <CarvisLink
      class="hover:text-blue-dark!"
      to="/dashboard"
    >
      <HomeOutline :size="24" />
    </CarvisLink>

    <template
      v-for="(breadcrumb, i) in breadcrumbs"
      :key="i"
    >
      <ChevronRight
        class="mx-1"
        :size="24"
      />
      <CarvisLink
        v-if="!('loading' in breadcrumb)"
        class="font-light! hover:text-blue-dark! hover:font-medium!"
        :to="breadcrumb.to"
      >
        {{ breadcrumb.text }}
      </CarvisLink>
      <span
        v-else
        class="w-fit bg-gray-900 overflow-hidden animate-pulse"
      >
        Lorem ipsum dolor sit amet
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import Breadcrumb from 'primevue/breadcrumb'
import { ref, watch } from 'vue'
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue'
import HomeOutline from 'vue-material-design-icons/HomeOutline.vue'
import { type RouteLocationNormalizedLoadedGeneric, type RouteLocationRaw, type RouteLocationResolvedGeneric, useRoute, useRouter } from 'vue-router'

import { useBreadcrumbsStore } from '@/stores/breadcrumbs-store'
import { useRealtimeStore } from '@/stores/realtime-store'

type Breadcrumb = { text: string; to: RouteLocationRaw, index: number } | { loading: boolean, index: number }

const route = useRoute()
const router = useRouter()
const breadcrumbs = ref<Breadcrumb[]>([])
const realtimeStore = useRealtimeStore()
const breadcrumbsStore = useBreadcrumbsStore()
const hasRealtimeSubscription = ref(false)

watch(breadcrumbs, (newBreadcrumbs) => {
  if (newBreadcrumbs.length === 0 && hasRealtimeSubscription.value) {
    realtimeStore.unsubscribe('BreadcrumbsBar')
    hasRealtimeSubscription.value = false
  } else if (newBreadcrumbs.length > 0 && !hasRealtimeSubscription.value) {
    const tables = breadcrumbsStore.tables
    realtimeStore.subscribe('BreadcrumbsBar', tables, async (table) => {
      await breadcrumbsStore.repopulateTableCache(table)
      await refreshBreadcrumbs(route)
    })
    hasRealtimeSubscription.value = true
  }
})

const refreshBreadcrumbs = async (route: RouteLocationNormalizedLoadedGeneric | RouteLocationResolvedGeneric): Promise<void> => {
  breadcrumbs.value = []
  await recursivelyResolveBreadcrumbs(route)
}

const recursivelyResolveBreadcrumbs = async (route: RouteLocationNormalizedLoadedGeneric | RouteLocationResolvedGeneric, index: number = 0): Promise<void> => {
  if (route.meta.parentComponent) {
    await recursivelyResolveBreadcrumbs(route.meta.parentComponent(router), index + 1)
  }

  if (route.meta.breadcrumb) {
    breadcrumbs.value.push({
      loading: true,
      index
    })
    route.meta.breadcrumb(router)
      .then(breadcrumb => {
        // replace the loading breadcrumb with the actual breadcrumb
        const idx = breadcrumbs.value.findIndex(b => b.index === index && 'loading' in b)
        if (idx !== -1) {
          breadcrumbs.value[idx] = {
            text: breadcrumb.text,
            to: breadcrumb.to,
            index: idx
          }
        }
      })
  }
}

watch(route, (newRoute) => refreshBreadcrumbs(newRoute))
</script>

<style scoped>
@reference "@/assets/main.css";

:deep(.router-link-active) {
  @apply font-medium!;
  @apply text-blue-dark;
}
</style>
